import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle,
  ClipboardList,
  Clock,
  FileCheck,
  FileText,
  FolderOpen,
  History,
  Layers,
  Lock,
  ScanText,
  Shield,
  Sparkles,
  Tag,
  Timer,
  Users,
  XCircle,
} from 'lucide-react';

import aiDocgenTemplates from '@src/assets/screenshots/ai-docgen-templates.webp';
import aiDocumentDiscovery from '@src/assets/screenshots/ai-document-discovery.webp';
import aiPiiSummaries from '@src/assets/screenshots/ai-pii-summaries.webp';
import aiReportSnapshots from '@src/assets/screenshots/ai-report-snapshots.webp';
import documentsAccess from '@src/assets/screenshots/documents-access.webp';
import documentsActivity from '@src/assets/screenshots/documents-activity.webp';
import documentsDashboard from '@src/assets/screenshots/documents-dashboard.webp';
import documentsDetail from '@src/assets/screenshots/documents-detail.webp';
import documentsPii from '@src/assets/screenshots/documents-pii.webp';
import documentsPreview from '@src/assets/screenshots/documents-preview.webp';
import documentsShare from '@src/assets/screenshots/documents-share.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const ORANGE = '#f59e0b';
const BG_COLOR = '#F8FAFC';

// Pain points for Document Management
const painPoints = [
  {
    icon: XCircle,
    problem: 'Documents Everywhere',
    description: 'Policies, contracts, and procedures scattered across SharePoint, email, and local drives with no central control.',
    solution: 'Unified document repository with folder structure, tagging, and powerful search across all content.',
  },
  {
    icon: Timer,
    problem: 'Manual Classification',
    description: 'Hours spent manually tagging, categorizing, and extracting metadata from uploaded documents.',
    solution: 'AI-powered auto-extraction identifies document type, classification, and key metadata from content.',
  },
  {
    icon: AlertTriangle,
    problem: 'Compliance Gaps',
    description: 'No visibility into document reviews, approvals, or expiration dates leading to audit findings.',
    solution: 'Workflow-based approvals with review schedules, expiration tracking, and full audit trails.',
  },
  {
    icon: Layers,
    problem: 'Version Chaos',
    description: 'Multiple versions of the same document with no clear "source of truth" or change history.',
    solution: 'Automatic version control with complete history, comparison, and rollback capabilities.',
  },
];

// Folder types
const folderTypes = [
  { name: 'Policies', items: 12, color: '#3b82f6', icon: FileCheck },
  { name: 'Procedures', items: 8, color: '#10b981', icon: ClipboardList },
  { name: 'Contracts', items: 15, color: '#8b5cf6', icon: FileText },
  { name: 'Reports', items: 24, color: '#f59e0b', icon: BarChart3 },
  { name: 'Compliance', items: 18, color: '#ef4444', icon: Shield },
  { name: 'HR', items: 9, color: '#06b6d4', icon: Users },
];

// Document statuses
const documentStatuses = [
  { status: 'Approved', count: 45, color: '#10b981' },
  { status: 'Draft', count: 12, color: '#f59e0b' },
  { status: 'In Review', count: 8, color: '#3b82f6' },
  { status: 'Rejected', count: 3, color: '#ef4444' },
  { status: 'Expired', count: 2, color: '#6b7280' },
];

// AI Capabilities
const aiCapabilities = [
  {
    icon: ScanText,
    title: 'Text Extraction',
    description: 'Automatically extract text content from PDFs, images, and scanned documents using OCR.',
    features: ['PDF parsing', 'OCR for images', 'Multi-language support'],
  },
  {
    icon: Sparkles,
    title: 'Auto-Classification',
    description: 'AI identifies document type, sensitivity level, and appropriate folder based on content.',
    features: ['Document type detection', 'Sensitivity classification', 'Smart folder suggestions'],
  },
  {
    icon: Shield,
    title: 'PII Detection',
    description: 'Scan documents for personally identifiable information and sensitive data.',
    features: ['SSN/ID detection', 'Credit card numbers', 'Email & phone detection'],
  },
  {
    icon: Tag,
    title: 'Metadata Extraction',
    description: 'Automatically extract key metadata like dates, names, and reference numbers.',
    features: ['Date extraction', 'Entity recognition', 'Reference number parsing'],
  },
];

// Capabilities
const capabilities = [
  {
    icon: FolderOpen,
    title: 'Folder Management',
    description: 'Organize documents in a hierarchical folder structure with customizable taxonomy.',
    features: ['Nested folders', 'Custom metadata', 'Bulk operations', 'Templates'],
  },
  {
    icon: FileCheck,
    title: 'Approval Workflows',
    description: 'Route documents through configurable approval chains with notifications and escalations.',
    features: ['Multi-stage approvals', 'Delegation', 'Deadline tracking', 'Audit trail'],
  },
  {
    icon: Lock,
    title: 'Access Control',
    description: 'Fine-grained permissions at folder and document level with role-based access.',
    features: ['Role-based access', 'Folder permissions', 'External sharing', 'Access logs'],
  },
  {
    icon: History,
    title: 'Version Control',
    description: 'Automatic versioning with complete history, comparison, and rollback.',
    features: ['Auto-versioning', 'Version comparison', 'Rollback', 'Change tracking'],
  },
];

// Dashboard metrics
const dashboardMetrics = [
  { label: 'Total Documents', value: '156', color: BOSSS_BLUE },
  { label: 'AI Drafts', value: '5', color: ORANGE },
  { label: 'Pending Review', value: '8', color: '#8b5cf6' },
  { label: 'Expiring Soon', value: '3', color: '#ef4444' },
];

export function DocumentsPage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fcd34d 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <FolderOpen className="w-8 h-8" style={{ color: `${ORANGE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <FileText className="w-6 h-6" style={{ color: `${ORANGE}40` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${ORANGE}15`, color: ORANGE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Bot className="w-4 h-4 mr-2" />
                AI-Powered Document Management
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Smart Documents. Zero Manual Work.
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS Documents uses AI to automatically classify, extract metadata, and
                organize your policies, contracts, and compliance documents—with built-in
                PII detection and approval workflows.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    <div className="text-xs text-gray-600">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: ORANGE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: ORANGE, color: ORANGE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Sales
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${ORANGE}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <FolderOpen className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Document Management</h3>
                      <p className="text-xs text-gray-500">AI-powered document intelligence</p>
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                    <Bot className="w-3 h-3" />
                    5 AI Drafts
                  </span>
                </div>

                {/* Folder Structure Preview */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-medium text-gray-700 mb-3">Folder Structure</p>

                  <div className="space-y-1 text-sm">
                    {folderTypes.map((folder, i) => (
                      <motion.div
                        key={i}
                        animate={{ opacity: 1, x: 0 }}
                        initial={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3, delay: 0.5 + i * 0.05 }}
                        className="flex items-center gap-2 py-1 px-2 hover:bg-white rounded transition-colors cursor-pointer"
                      >
                        <folder.icon className="w-4 h-4" style={{ color: folder.color }} />
                        <span className="text-gray-700">{folder.name}</span>

                        <span className="text-xs text-gray-400 ml-auto">
                          {folder.items}
                          {' '}
                          items
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Document Status */}
                <div className="grid grid-cols-3 gap-2">
                  {documentStatuses.slice(0, 3).map((status, i) => (
                    <div key={i} className="text-center p-2 bg-gray-50 rounded-lg">
                      <p className="text-lg font-bold" style={{ color: status.color }}>{status.count}</p>
                      <p className="text-xs text-gray-500">{status.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/documents" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              End Document Management Frustration
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional document management creates chaos. Here&apos;s how BOSSS Documents
              brings order with AI-powered automation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-sm text-gray-500">{point.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${ORANGE}08` }} className="flex items-start gap-3 p-4 rounded-lg">
                      <CheckCircle style={{ color: ORANGE }} className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{point.solution}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Intelligent document management with built-in PII detection, folder organization,
              and AI-powered classification—all in one unified interface.
            </p>
          </motion.div>

          <motion.div
            className="relative"
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
          >
            <ScreenshotGallery
              urlPath="documents"
              accentColor={ORANGE}
              screenshots={[
                { src: documentsDashboard, label: 'Library', alt: 'BOSSS document management library with folder tree, classification and per-entity ownership' },
                { src: documentsDetail, label: 'Details', alt: 'BOSSS document details panel with owner, entity, type, classification, status and attached file' },
                { src: documentsPreview, label: 'Preview', alt: 'BOSSS in-app document preview without leaving the library' },
                { src: documentsPii, label: 'PII Detection', alt: 'BOSSS automated PII detection summary with risk score and confirmed findings by type, category and severity' },
                { src: documentsAccess, label: 'Access', alt: 'BOSSS per-document access control showing who can view and edit' },
                { src: documentsShare, label: 'Sharing', alt: 'BOSSS document sharing with external links and expiry controls' },
                { src: documentsActivity, label: 'Activity', alt: 'BOSSS document activity trail recording every view, edit and download' },
                { src: aiPiiSummaries, label: 'PII Register', alt: 'BOSSS PII detection register across every scanned document with finding counts, risk level, overall risk score and review status' },
                { src: aiDocumentDiscovery, label: 'AI Discovery', alt: 'BOSSS AI document discovery surfacing policy and audit evidence inside a compliance certification project' },
                { src: aiDocgenTemplates, label: 'Report Templates', alt: 'BOSSS document generation templates for access review, penetration test and risk assessment reporting' },
                { src: aiReportSnapshots, label: 'Generated Reports', alt: 'BOSSS generated report snapshots with source template, output format and the analyst who generated them' },
              ]}
            />
          </motion.div>

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Shield, label: 'PII Detection', value: 'Real-time' },
              { icon: FolderOpen, label: 'Folder Management', value: 'Unlimited' },
              { icon: Bot, label: 'AI Classification', value: 'Automatic' },
              { icon: Lock, label: 'Access Control', value: 'Role-based' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: ORANGE }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div
              style={{ backgroundColor: `${ORANGE}15`, color: ORANGE }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Bot className="w-4 h-4 mr-2" />
              AI-Powered
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Let AI Do the Heavy Lifting
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Upload a document and watch AI automatically extract text, classify content,
              detect sensitive data, and suggest metadata.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-orange-50 to-amber-50">
                  <CardHeader className="pb-2">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform bg-white shadow-sm">
                      <capability.icon className="w-6 h-6" style={{ color: ORANGE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-orange-600 transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

                    <ul className="space-y-1">
                      {capability.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-500">
                          <Sparkles className="w-3 h-3 mr-2" style={{ color: ORANGE }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Draft Demo Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div
                style={{ backgroundColor: `${ORANGE}15`, color: ORANGE }}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                AI Draft Review
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Upload → AI Extracts → You Review
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Simply upload a PDF or document. Our AI reads the content, extracts key information,
                and creates a draft record for your review. Accept, modify, or reject—you&apos;re always in control.
              </p>

              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: ORANGE }} />
                  Automatic document type detection
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: ORANGE }} />
                  Sensitivity classification (Internal, Confidential, Restricted)
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: ORANGE }} />
                  PII scanning with redaction suggestions
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: ORANGE }} />
                  One-click approve or manual adjustment
                </li>
              </ul>
            </div>

            {/* AI Draft Preview Card */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Bot className="w-5 h-5" style={{ color: ORANGE }} />
                    AI Draft Review
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-200 text-orange-800">BETA</span>
                  </h4>
                </div>

                {/* Simulated AI Extraction */}
                <div className="bg-white rounded-lg p-4 mb-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Source Document</p>

                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <FileText className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-gray-700">Employee_Internet_Policy.pdf</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">Extracted Data</p>

                  <div className="space-y-3">
                    {[
                      { label: 'Type', value: 'Policy', color: ORANGE },
                      { label: 'Status', value: 'Draft', color: '#6b7280' },
                      { label: 'Visibility', value: 'Internal', color: '#3b82f6' },
                      { label: 'Classification', value: 'Confidential', color: '#ef4444' },
                    ].map((field, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{field.label}</span>

                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${field.color}15`, color: field.color }}>
                          {field.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    Reject
                  </button>

                  <button className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    Save Draft
                  </button>

                  <button style={{ backgroundColor: ORANGE }} className="flex-1 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors">
                    Approve & Create
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Document Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Document Lifecycle
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From upload to archive—manage the entire document lifecycle with workflows,
              access control, and full audit trails.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-2">
                    <div
                      style={{ backgroundColor: `${ORANGE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <capability.icon className="w-6 h-6" style={{ color: ORANGE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-orange-600 transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

                    <ul className="space-y-1">
                      {capability.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-500">
                          <CheckCircle className="w-3 h-3 mr-2" style={{ color: ORANGE }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={8}
        accentColor="#8b5cf6"
        agentName="Document AI"
        description="AI-powered document intelligence. From automatic classification to PII detection—let AI handle the heavy lifting."
        exampleQueries={[
          '"Extract metadata from this contract"',
          '"Scan this document for PII"',
          '"Classify this policy document"',
          '"Find all documents expiring this month"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready for Intelligent Document Management?
            </h2>

            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS Documents can automate classification, ensure compliance,
              and eliminate document chaos with AI.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="px-8 py-4 text-lg rounded-lg transition-all duration-200 border-white/80 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Talk to Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: FileText, value: 'Unlimited', label: 'Document Storage' },
              { icon: Bot, value: 'AI-Powered', label: 'Classification' },
              { icon: Shield, value: 'Built-In', label: 'Audit Trails' },
              { icon: Clock, value: 'Automated', label: 'Workflows' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <stat.icon style={{ color: BOSSS_BLUE }} className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default DocumentsPage;
